import cx from 'classnames'
import parse, { domToReact, Element } from 'html-react-parser'
import { useContext } from 'react'

import { getFormattedValueWithThousandSeparator } from '@js/common/getFormattedValueWithThousandSeparator'
import { useToggle } from '@js/common/hooks/useToggle'

import { QuizContext } from '@f/applications/QuizApp/context/QuizContext'
import { NestedStepValue } from '@f/applications/QuizApp/partials/types'
import Icon from '@f/elements/Icon/Icon'

import { roundToTenThousand } from '../../QuestionBudget/utils'

export const FinancePartnerDescription = ({
  children
}: {
  children: string
}) => {
  const [isDescriptionVisible, toggleDescription] = useToggle(false)

  return (
    <div
      className='RecommendedFinance_description-container f-t-mini'
      onClick={() => toggleDescription()}>
      <div className={cx('f-d-flr f-fl-al-ce')}>
        <Icon
          icon={isDescriptionVisible ? 'down-arrow-16px' : 'right-arrow-16px'}
          className='f-c-st-ter f-m-r-010 f-icon-010'
        />
        <span className='f-c-fo-ter'>Weitere Infos zum Anbieter</span>
      </div>
      <div
        className={cx(
          'RecommendedFinance_description-wrapper f-t-mini',
          isDescriptionVisible ? 'expanded' : 'collapsed'
        )}>
        <div className='RecommendedFinance_description'>
          <ParsedDescription description={children} />
        </div>
      </div>
    </div>
  )
}

export const ParsedDescription = ({ description }: { description: string }) => {
  return parse(description, {
    trim: true,
    replace: domNode => {
      const element = (domNode as unknown) as Element
      if (element.type === 'tag' && element.children && element.name === 'p') {
        return <p suppressHydrationWarning>{domToReact(element.children)}</p>
      }
      return domNode
    }
  })
}

export interface FinancePartner {
  public_id: string
  uuid: string
  partner_name: string
  partner_tagline: string
  partner_logo_url: string
  contact_salutation?: string
  contact_name: string
  contact_street: string
  contact_postal_code: string
  contact_city: string
  partner_description: string
}

export interface FinanceLeadsFoundProps {
  financePartners: FinancePartner[]
}

export const RecommendedFinanceTile = ({ data }: { data: FinancePartner }) => {
  return (
    <div className='RecommendedFinance_card-container'>
      <div>
        <div className='RecommendedFinance_card-header'>
          <div className='f-d-flr f-fl-al-ce f-shadow-action f-c-ba-whi RecommendedFinance_partner-logo-wrapper'>
            <img
              src={data.partner_logo_url}
              alt={`Logo von ${data.partner_name}`}
              className='f-d-bl RecommendedFinance_partner-logo'
              loading='lazy'
            />
          </div>
          <div style={{ alignSelf: 'flex-start' }} className='f-overflow'>
            <div className='f-t-tminor f-c-fo-pri f-t-ellip f-!underline'>
              {data.partner_name}
            </div>
            <div className='f-t-mini RecommendedFinance_avatar-description'>
              {data.partner_tagline}
            </div>
          </div>
        </div>

        <FinancePartnerDescription>
          {data.partner_description}
        </FinancePartnerDescription>
      </div>
      <div className='f-c-ba-bac025 f-p-a-020 f-d-flr f-fl-al-en'>
        <div className='RecommendedFinance_avatar-wrapper'>
          <span className={'f-d-inbl f-bo-round f-c-fi-pri'}>
            <Icon icon={'author-64px-fill'} className={`f-c-fi-pri`} />
          </span>
        </div>
        <div className='f-fl-default'>
          <div className='f-t-body f-t-lineheight:133'>Ihr Kontakt</div>
          <div className='f-t-body f-t-bold RecommendedFinance_elip-1 f-t-lineheight:133'>
            {`${data.contact_salutation ? data.contact_salutation + ' ' : ''}${
              data.contact_name
            }`}
          </div>
          <div className='f-t-mini f-t-lineheight:133'>
            {`${data.contact_street}, ${data.contact_city} ${data.contact_postal_code}`}
          </div>
        </div>
      </div>
    </div>
  )
}

const checkListLabel = [
  'Monatliches Nettoeinkommen:',
  'Eigenkapital:',
  'Maklerprovision:',
  'Aktueller 10-Jahres-Zinssatz:'
]

interface FinancialDetailsValues extends NestedStepValue {
  name:
    | 'monthly_income'
    | 'equity'
    | 'agent_fee'
    | 'ten_year_rate'
    | 'monthly_income_slider'
    | 'equity_slider'
  value: string
}

export const FinanceDetailsCard = () => {
  const context = useContext(QuizContext)

  const financialDetailsValues: FinancialDetailsValues[] = context.data.steps.find(
    ({ id }: { id: string }) => id === 'financial_details'
  )?.values

  const monthlyIncome = parseInt(
    financialDetailsValues?.find(({ name }) => name === 'monthly_income')
      ?.value ||
      context.data.steps.find(
        ({ id }: { id: string }) => id === 'monthly_income_slider'
      )?.values[0].value ||
      0
  )
  const equity = parseInt(
    financialDetailsValues?.find(({ name }) => name === 'equity')?.value ||
      context.data.steps.find(
        ({ id }: { id: string }) => id === 'equity_slider'
      )?.values[0].value ||
      0
  )
  const agentFee = 375 / 10000
  const tenYearRate = 315 / 10000

  const purchasePriceLow =
    ((monthlyIncome * 12 * 0.3) / (tenYearRate + 0.02) + equity) /
    (1 + agentFee)
  const purchasePriceHigh =
    ((monthlyIncome * 12 * 0.4) / (tenYearRate + 0.02) + equity) /
    (1 + agentFee)

  return (
    <div className='QuestionSuccessCard-wrapper'>
      <div className='f-t-mini f-t-bold'>Ihr idealer Finanzierungsrahmen</div>
      <div className='f-t-tsection f-m-t-010 f-m-b-020'>
        {`${getFormattedValueWithThousandSeparator(
          roundToTenThousand(purchasePriceLow)
        )}€ -
          ${getFormattedValueWithThousandSeparator(
            roundToTenThousand(purchasePriceHigh)
          )}€`}
      </div>
      <div className='f-t-mini f-t-bold'>
        So wurde Ihr Finanzierungsrahmen berechnet:
      </div>

      <div className='f-d-flc f-fl-gap-010 f-m-t-010'>
        <CalculationExplanation
          equity={equity}
          monthlyIncome={monthlyIncome}
          agentFee={`${agentFee * 100}%`}
          interestRate={`${tenYearRate * 100}%`}
        />
      </div>
    </div>
  )
}

const CalculationExplanation = ({
  monthlyIncome,
  equity,
  agentFee,
  interestRate
}: {
  monthlyIncome: number
  equity: number
  agentFee: string
  interestRate: string
}) => {
  const checkListValues = [
    `${getFormattedValueWithThousandSeparator(monthlyIncome)} €`,
    `${getFormattedValueWithThousandSeparator(equity)} €`,
    agentFee,
    interestRate
  ]

  return (
    <div className='f-d-flc f-fl-gap-010 f-m-t-010'>
      {checkListLabel.map((item, index) => (
        <div className='f-d-flr f-fl-al-ce' key={`checklist_element_${index}`}>
          <div className='f-fl-no'>
            <Icon
              icon='check-16px'
              className='f-icon-025 f-c-st-suc100 f-m-r-020'
            />
          </div>
          <div className='f-t-mini'>
            {item} <b>{checkListValues[index]}</b>
          </div>
        </div>
      ))}
    </div>
  )
}
